import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useUpdatePreferencesMutation } from '@platform/state/api';
import { useAppDispatch, useAppSelector } from '@platform/state/hooks';
import { selectSession } from '@platform/state/selectors';
import { toastShown } from '@platform/state/slices/uiSlice';
import { pinnedKeys } from './pinnedKeys';
import { useShellNav } from './useShellNav';

/**
 * Закріплені модулі робочого простору та їх перемикання зіркою (сторінка «Усі модулі»): зберігається в налаштуваннях користувача
 * (PUT /me/preferences → pinnedModules[ws]); доки користувач нічого не закріпив, відлік іде від типових основних модулів. Повертає
 * [закріплені, перемкнути модуль, зберегти весь список (порожній — повернутися до типових)].
 */
export function usePins(ws: string): [string[], (key: string) => void, (keys: string[]) => void] {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const session = useAppSelector(selectSession);
  const { me, catalog } = useShellNav(ws);
  const [update] = useUpdatePreferencesMutation();
  const pinned = pinnedKeys(me, catalog, ws);
  const save = useCallback((next: string[]) => {
    if (!me) return;
    update({ locale: session.lang, colorScheme: session.colorScheme, pinnedModules: { ...me.pinnedModules, [ws]: next } }).unwrap()
      .catch((e: { message?: string }) => dispatch(toastShown({ kind: 'error', text: t('allModules.pinFailed', { message: e?.message ?? '' }) })));
  }, [dispatch, me, session.colorScheme, session.lang, t, update, ws]);
  const toggle = useCallback((key: string) => save(pinned.includes(key) ? pinned.filter((k) => k !== key) : [...pinned, key]), [pinned, save]);
  return [pinned, toggle, save];
}
